import { cacheKeys } from "@constants/cache-keys";
import { ENDPOINT_CACHE } from "@constants/urls";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Platform } from "react-native";

type UseCacheOptions<T> = {
	key: string;
	defaultData: T;
	enabled?: boolean;
	staleTime?: number;
	shouldInvalidate?: boolean;
	shouldOptimisticUpdate?: boolean;
};

const useCache = <T>({
	key,
	defaultData,
	enabled = true,
	staleTime = 0,
	shouldInvalidate = true,
	shouldOptimisticUpdate = false,
}: UseCacheOptions<T>) => {
	const queryClient = useQueryClient();

	const {
		data: cacheData,
		isLoading,
		error,
		isRefetching,
	} = useQuery({
		queryKey: [cacheKeys.cache, key],
		queryFn: async (): Promise<T> => {
			if (!key) {
				return defaultData;
			}

			try {
				const response = await axios.get(ENDPOINT_CACHE, {
					params: { key },
				});
				return response.data.data || defaultData;
			} catch (error) {
				if (axios.isAxiosError(error) && error.response?.status === 404) {
					return defaultData;
				}
				throw new Error("Failed to fetch cache data");
			}
		},
		enabled: !!key && enabled,
		staleTime,
	});

	const setCacheMutation = useMutation({
		mutationFn: async (data: Partial<T>) => {
			if (!key) {
				throw new Error("Cache key not available");
			}

			const cachedData = queryClient.getQueryData<T>([cacheKeys.cache, key]);
			const currentData = cachedData || defaultData;
			const updatedData = { ...currentData, ...data, platform: Platform.OS };

			await axios.post(ENDPOINT_CACHE, {
				key,
				data: updatedData,
			});

			return updatedData;
		},

		onMutate: async (newData: Partial<T>) => {
			if (!shouldOptimisticUpdate) {
				return;
			}

			await queryClient.cancelQueries({ queryKey: [cacheKeys.cache, key] });

			const previousData = queryClient.getQueryData<T>([cacheKeys.cache, key]);

			const currentData = previousData || defaultData;
			const optimisticData = { ...currentData, ...newData };
			queryClient.setQueryData([cacheKeys.cache, key], optimisticData);

			return { previousData };
		},
		// onError: (_err, _newData, context) => {
		// 	if (context?.previousData) {
		// 		queryClient.setQueryData([cacheKeys.cache, key], context.previousData);
		// 	}
		// },
		onSettled: () => {
			if (shouldInvalidate) {
				queryClient.invalidateQueries({ queryKey: [cacheKeys.cache, key] });
			}
		},
	});

	const deleteCacheMutation = useMutation({
		mutationFn: async () => {
			if (!key) {
				throw new Error("Cache key not available");
			}

			await axios.delete(ENDPOINT_CACHE, {
				data: { key },
			});
		},
		onSuccess: () => {
			queryClient.removeQueries({
				queryKey: [cacheKeys.cache, key],
			});
		},
	});

	return {
		data: cacheData,
		isLoading,
		error,
		setCache: setCacheMutation.mutateAsync,
		deleteCache: deleteCacheMutation.mutateAsync,
		isUpdating: setCacheMutation.isPending,
		isDeleting: deleteCacheMutation.isPending,
		isRefetching,
	};
};

export default useCache;
